import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  clubToday,
  formatClubTime,
  formatPeopleCount,
  getPanelState,
  isClubMember,
  isDateSelectable,
  panelTarget,
} from '../../domain/matching/matching';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectMatchingEnabled,
  selectMatchingPanelByDate,
} from '../../store/selectors/matchingSelectors';
import { fetchMatchingPanel, fetchMatchingStatus } from '../../store/thunks';

import './matching.css';

/** Full-width "Поиск игроков" status panel on the plan page. */
export function MatchingPanel() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role = useAppSelector((s) => s.auth.user?.role);
  const dateKey = useAppSelector((s) => s.ui.selectedDate);
  const panelByDate = useAppSelector(selectMatchingPanelByDate);
  const member = isClubMember(role);
  const enabled = useAppSelector(selectMatchingEnabled);

  React.useEffect(() => {
    void dispatch(fetchMatchingStatus());
  }, [dispatch]);

  React.useEffect(() => {
    if (member && enabled !== false) void dispatch(fetchMatchingPanel());
  }, [dispatch, member, enabled, dateKey]);

  if (enabled === false) return null;

  if (!member) {
    return (
      <div className="matching-panel matching-panel--disabled" aria-disabled="true">
        <div className="matching-panel__title">🎲 Поиск игроков</div>
        <div className="matching-panel__text">Доступно членам клуба</div>
      </div>
    );
  }

  if (!isDateSelectable(dateKey, clubToday())) {
    return (
      <div className="matching-panel matching-panel--disabled" aria-disabled="true">
        <div className="matching-panel__title">🎲 Поиск игроков</div>
        <div className="matching-panel__text">Поиск компании доступен на 7 дней вперёд</div>
      </div>
    );
  }

  const state = getPanelState(panelByDate[dateKey]);

  let headline = 'Поиск игроков';
  let text = 'Загрузка…';
  let modifier = '';

  if (state?.kind === 'none') {
    text = `Ищут пати: ${formatPeopleCount(state.activeCount)} · ${state.hasKeyHolder ? '🔑 есть ключ' : '🔑 нужен ключ'}`;
  } else if (state?.kind === 'active') {
    headline = 'Поиск активен';
    modifier = 'matching-panel--active';
    text = state.progress
      ? `Найдено: ${state.progress.size} из ${state.progress.min}–${state.progress.max}`
      : 'Ищем подходящую компанию…';
  } else if (state?.kind === 'matched') {
    headline = 'Компания найдена';
    modifier = 'matching-panel--matched';
    text = state.confirmationDeadline
      ? `Подтвердите участие до ${formatClubTime(state.confirmationDeadline)}`
      : 'Подтвердите участие в мероприятии';
  } else if (state?.kind === 'formed') {
    headline = 'Стол собран';
    modifier = 'matching-panel--formed';
    text = state.startAt ? `Начало в ${formatClubTime(state.startAt)}` : 'Ждём вас за столом';
  }

  return (
    <button
      type="button"
      className={`matching-panel matching-panel--action ${modifier}`.trim()}
      onClick={() => navigate(panelTarget(state))}
    >
      <div className="matching-panel__body">
        <div className="matching-panel__title">🎲 {headline}</div>
        <div className="matching-panel__text">{text}</div>
      </div>
      <span className="matching-panel__chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
